import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, AlertCircle, Search, X, ChevronRight, Clock, Phone, Mail, 
  GraduationCap, School, Calendar, Plus, Check, Loader2, ArrowLeft,
  UserPlus, MessageCircle, RefreshCw
} from 'lucide-react';

// Status badge colors
const STATUS_COLORS = {
  trial: { bg: '#FEF3C7', text: '#92400E' },
  active: { bg: '#D1FAE5', text: '#065F46' },
  grace: { bg: '#FEE2E2', text: '#991B1B' },
  churned: { bg: '#F3F4F6', text: '#6B7280' },
};

const ISSUE_TYPES = [
  { value: 'parent_child_link', label: 'Parent-Child Link' },
  { value: 'payment_access', label: 'Payment / Access' },
  { value: 'account_access', label: 'Account Access' },
  { value: 'content_issue', label: 'Content Issue' },
  { value: 'trial_extension', label: 'Trial Extension Request' },
  { value: 'plan_query', label: 'Plan Query' },
  { value: 'other', label: 'Other' },
];

function StatusBadge({ status }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.churned;
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {status || 'unknown'}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ServiceDashboard({ onNavigate }) {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('clients'); // clients | issues
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [issueStatusFilter, setIssueStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  
  // Modal states
  const [showExtendTrialModal, setShowExtendTrialModal] = useState(false);
  const [showLinkChildModal, setShowLinkChildModal] = useState(false);
  const [showNewIssueForm, setShowNewIssueForm] = useState(false);
  
  // Form states
  const [extendDays, setExtendDays] = useState(3);
  const [extendReason, setExtendReason] = useState('');
  const [childEmail, setChildEmail] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [linkDirection, setLinkDirection] = useState('child'); // 'child' or 'parent'
  const [newIssueType, setNewIssueType] = useState('other');
  const [newIssueDescription, setNewIssueDescription] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // Check access
  useEffect(() => {
    if (user && !['admin', 'service_poc'].includes(user.role)) {
      onNavigate('auth');
    }
  }, [user, onNavigate]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await api.get(`/service/users?${params.toString()}`);
      setUsers(res.data.users || []);
      // Update stats with counts from the users API
      if (res.data.counts) {
        setStats(prev => ({
          ...prev,
          trial_users: res.data.counts.trial || 0,
          active_users: res.data.counts.active || 0,
          grace_users: res.data.counts.grace || 0,
          churned_users: res.data.counts.churned || 0,
          total_users: (res.data.counts.trial || 0) + (res.data.counts.active || 0) + (res.data.counts.grace || 0) + (res.data.counts.churned || 0)
        }));
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
    setLoading(false);
  }, [api, statusFilter, searchQuery]);

  // Fetch issues
  const fetchIssues = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (issueStatusFilter !== 'all') params.append('status', issueStatusFilter);
      
      const res = await api.get(`/service/issues?${params.toString()}`);
      setIssues(res.data.issues || []);
    } catch (err) {
      console.error('Error fetching issues:', err);
    }
  }, [api, issueStatusFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/service/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [api]);

  // Fetch user details
  const fetchUserDetails = async (userId) => {
    try {
      const res = await api.get(`/service/users/${userId}`);
      setSelectedUser(res.data);
      setShowDetailPanel(true);
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);

  useEffect(() => {
    if (activeTab === 'issues') {
      fetchIssues();
    }
  }, [activeTab, fetchIssues]);

  // Action handlers
  const handleExtendTrial = async () => {
    setActionLoading(true);
    setActionError('');
    setActionSuccess('');
    try {
      const res = await api.post(`/service/users/${selectedUser.id}/extend-trial`, {
        days: extendDays,
        reason: extendReason
      });
      setActionSuccess(`Trial extended by ${extendDays} days. New end date: ${formatDate(res.data.new_trial_end)}`);
      setShowExtendTrialModal(false);
      setExtendReason('');
      fetchUserDetails(selectedUser.id);
      fetchUsers();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to extend trial');
    }
    setActionLoading(false);
  };

  const handleLinkChild = async () => {
    setActionLoading(true);
    setActionError('');
    setActionSuccess('');
    try {
      const payload = linkDirection === 'child' 
        ? { child_email: childEmail }
        : { parent_email: parentEmail };
      
      const res = await api.post(`/service/users/${selectedUser.id}/link-child`, payload);
      setActionSuccess(res.data.message);
      setShowLinkChildModal(false);
      setChildEmail('');
      setParentEmail('');
      setLinkDirection('child');
      fetchUserDetails(selectedUser.id);
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to link accounts');
    }
    setActionLoading(false);
  };

  const handleCreateIssue = async () => {
    setActionLoading(true);
    setActionError('');
    setActionSuccess('');
    try {
      await api.post('/service/issues', {
        user_id: selectedUser.id,
        type: newIssueType,
        description: newIssueDescription
      });
      setActionSuccess('Issue logged successfully');
      setShowNewIssueForm(false);
      setNewIssueDescription('');
      setNewIssueType('other');
      fetchUserDetails(selectedUser.id);
      fetchIssues();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to create issue');
    }
    setActionLoading(false);
  };

  const handleResolveIssue = async (issueId) => {
    try {
      await api.patch(`/service/issues/${issueId}`, { status: 'resolved' });
      fetchIssues();
      if (selectedUser) {
        fetchUserDetails(selectedUser.id);
      }
    } catch (err) {
      console.error('Error resolving issue:', err);
    }
  };

  // If user doesn't have access, show nothing (will redirect)
  if (!user || !['admin', 'service_poc'].includes(user.role)) {
    return null;
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F1F5F9' }}>
      {/* Sidebar */}
      <div className="w-64 fixed left-0 top-0 h-full" style={{ backgroundColor: '#0F172A' }}>
        <div className="p-6">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#7C3AED' }}>
              <span className="text-white text-xl font-bold">S</span>
            </div>
            <span className="text-xl font-bold text-white">StudyClaw</span>
          </div>
          
          {/* Navigation */}
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('clients')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'clients' 
                  ? 'bg-violet-600 text-white' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              data-testid="nav-clients"
            >
              <Users className="w-5 h-5" />
              <span>Clients</span>
            </button>
            <button
              onClick={() => setActiveTab('issues')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'issues' 
                  ? 'bg-violet-600 text-white' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              data-testid="nav-issues"
            >
              <AlertCircle className="w-5 h-5" />
              <span>Issues</span>
              {stats?.open_issues > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {stats.open_issues}
                </span>
              )}
            </button>
          </nav>
        </div>
        
        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{user?.name || 'Admin'}</p>
              <p className="text-slate-500 text-xs">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={() => onNavigate('dashboard')}
            className="mt-4 w-full flex items-center justify-center gap-2 text-slate-400 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 flex-1 p-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-slate-500 text-sm">Total Users</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border-l-4" style={{ borderColor: STATUS_COLORS.trial.text }}>
              <p className="text-slate-500 text-sm">Trial</p>
              <p className="text-2xl font-bold text-slate-900">{stats.trial_users}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border-l-4" style={{ borderColor: STATUS_COLORS.active.text }}>
              <p className="text-slate-500 text-sm">Active/Paid</p>
              <p className="text-2xl font-bold text-slate-900">{stats.active_users}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border-l-4" style={{ borderColor: STATUS_COLORS.grace.text }}>
              <p className="text-slate-500 text-sm">Grace</p>
              <p className="text-2xl font-bold text-slate-900">{stats.grace_users}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border-l-4" style={{ borderColor: STATUS_COLORS.churned.text }}>
              <p className="text-slate-500 text-sm">Churned</p>
              <p className="text-2xl font-bold text-slate-900">{stats.churned_users}</p>
            </div>
          </div>
        )}

        {activeTab === 'clients' && (
          <div className="bg-white rounded-2xl shadow-sm">
            {/* Top Bar */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search name, phone, email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-sm w-64 focus:outline-none focus:border-violet-500"
                    data-testid="search-input"
                  />
                </div>
                
                {/* Filter Tabs */}
                <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
                  {['all', 'trial', 'active', 'grace', 'churned'].map(status => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all capitalize ${
                        statusFilter === status 
                          ? 'bg-white text-slate-900 shadow-sm' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid={`filter-${status}`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              
              <span className="text-sm text-slate-500">
                {users.length} client{users.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Client</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Phone</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Grade</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">School</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Trial Day</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Plan</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Last Active</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Issues</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="py-12 text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto" />
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-12 text-center text-slate-500">
                        No clients found
                      </td>
                    </tr>
                  ) : (
                    users.map(u => (
                      <tr 
                        key={u.id} 
                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => fetchUserDetails(u.id)}
                        data-testid={`user-row-${u.id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-semibold text-sm">
                              {u.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{u.name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{u.phone || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{u.class_name || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{u.school || '-'}</td>
                        <td className="py-3 px-4"><StatusBadge status={u.plan_status} /></td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {u.trial_day ? `Day ${u.trial_day}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600 capitalize">{u.plan || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{u.last_active_relative || 'Never'}</td>
                        <td className="py-3 px-4">
                          {u.open_issue_count > 0 ? (
                            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">
                              {u.open_issue_count}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'issues' && (
          <div className="bg-white rounded-2xl shadow-sm">
            {/* Top Bar */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Service Issues</h2>
              
              {/* Filter Tabs */}
              <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
                {['all', 'open', 'resolved'].map(status => (
                  <button
                    key={status}
                    onClick={() => setIssueStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all capitalize ${
                      issueStatusFilter === status 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`issue-filter-${status}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Issues Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Client</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Description</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {issues.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-500">
                        No issues found
                      </td>
                    </tr>
                  ) : (
                    issues.map(issue => (
                      <tr 
                        key={issue.id} 
                        className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`issue-row-${issue.id}`}
                      >
                        <td className="py-3 px-4">
                          <button
                            onClick={() => fetchUserDetails(issue.user_id)}
                            className="text-violet-600 hover:text-violet-700 font-medium text-sm"
                          >
                            {issue.user_name || 'Unknown'}
                          </button>
                          <p className="text-xs text-slate-500">{issue.user_phone}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs">
                            {ISSUE_TYPES.find(t => t.value === issue.type)?.label || issue.type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate">
                          {issue.description?.slice(0, 80)}{issue.description?.length > 80 ? '...' : ''}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{formatDate(issue.created_at)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            issue.status === 'open' 
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {issue.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {issue.status === 'open' && (
                            <button
                              onClick={() => handleResolveIssue(issue.id)}
                              className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1"
                              data-testid={`resolve-${issue.id}`}
                            >
                              <Check className="w-4 h-4" />
                              Resolve
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Client Detail Panel */}
      <AnimatePresence>
        {showDetailPanel && selectedUser && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40"
              onClick={() => setShowDetailPanel(false)}
            />
            
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 overflow-y-auto"
              data-testid="detail-panel"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-slate-100 p-6 z-10">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => setShowDetailPanel(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-xl">
                    {selectedUser.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-slate-900">{selectedUser.name || 'Unknown'}</h2>
                      <StatusBadge status={selectedUser.plan_status} />
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                      {selectedUser.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {selectedUser.phone}
                        </span>
                      )}
                      {selectedUser.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {selectedUser.email}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                      {selectedUser.class_name && (
                        <span className="flex items-center gap-1">
                          <GraduationCap className="w-3 h-3" /> {selectedUser.class_name}
                        </span>
                      )}
                      {selectedUser.school && (
                        <span className="flex items-center gap-1">
                          <School className="w-3 h-3" /> {selectedUser.school}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Action Success/Error Messages */}
                {actionSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
                    {actionSuccess}
                  </div>
                )}
                {actionError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                    {actionError}
                  </div>
                )}

                {/* Plan Section */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Plan Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Current Plan</p>
                      <p className="font-medium text-slate-900 capitalize">{selectedUser.plan || selectedUser.plan_status || 'None'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">
                        {selectedUser.plan_status === 'trial' ? 'Trial Ends' : 'Next Billing'}
                      </p>
                      <p className="font-medium text-slate-900">
                        {formatDate(selectedUser.trial_end || selectedUser.next_billing_date)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Sessions</p>
                      <p className="font-medium text-slate-900">{selectedUser.session_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Last Active</p>
                      <p className="font-medium text-slate-900">{selectedUser.last_active_relative || 'Never'}</p>
                    </div>
                    {selectedUser.trial_day && (
                      <div>
                        <p className="text-slate-500">Trial Day</p>
                        <p className="font-medium text-slate-900">Day {selectedUser.trial_day}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-900">Actions</h3>
                  
                  <button
                    onClick={() => {
                      setShowExtendTrialModal(true);
                      setActionError('');
                      setActionSuccess('');
                    }}
                    className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:border-violet-300 hover:bg-violet-50 transition-colors"
                    data-testid="action-extend-trial"
                  >
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-900">Extend Trial</p>
                      <p className="text-xs text-slate-500">Add extra days to trial period</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setShowLinkChildModal(true);
                      setActionError('');
                      setActionSuccess('');
                    }}
                    className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:border-violet-300 hover:bg-violet-50 transition-colors"
                    data-testid="action-link-child"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-900">Fix Parent-Child Link</p>
                      <p className="text-xs text-slate-500">Link a child account to a parent</p>
                    </div>
                  </button>
                </div>

                {/* Service Issues Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">Service Issues</h3>
                    <button
                      onClick={() => {
                        setShowNewIssueForm(true);
                        setActionError('');
                        setActionSuccess('');
                      }}
                      className="text-violet-600 hover:text-violet-700 text-sm font-medium flex items-center gap-1"
                      data-testid="log-new-issue"
                    >
                      <Plus className="w-4 h-4" />
                      Log New Issue
                    </button>
                  </div>

                  {/* New Issue Form */}
                  {showNewIssueForm && (
                    <div className="bg-slate-50 rounded-xl p-4 mb-4">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Issue Type</label>
                          <select
                            value={newIssueType}
                            onChange={(e) => setNewIssueType(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-violet-500"
                            data-testid="issue-type-select"
                          >
                            {ISSUE_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                          <textarea
                            value={newIssueDescription}
                            onChange={(e) => setNewIssueDescription(e.target.value)}
                            placeholder="Describe the issue..."
                            rows={3}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-violet-500 resize-none"
                            data-testid="issue-description"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCreateIssue}
                            disabled={!newIssueDescription || actionLoading}
                            className="flex-1 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                            data-testid="submit-issue"
                          >
                            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            Submit
                          </button>
                          <button
                            onClick={() => setShowNewIssueForm(false)}
                            className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Issues List */}
                  <div className="space-y-2">
                    {selectedUser.service_issues?.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">No issues logged</p>
                    ) : (
                      selectedUser.service_issues?.map(issue => (
                        <div key={issue.id} className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs">
                              {ISSUE_TYPES.find(t => t.value === issue.type)?.label || issue.type}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              issue.status === 'open' 
                                ? 'bg-amber-100 text-amber-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {issue.status}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">{issue.description}</p>
                          <p className="text-xs text-slate-400 mt-1">{formatDate(issue.created_at)}</p>
                          {issue.status === 'open' && (
                            <button
                              onClick={() => handleResolveIssue(issue.id)}
                              className="mt-2 text-green-600 hover:text-green-700 text-xs font-medium"
                            >
                              Mark Resolved
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Service Actions History */}
                {selectedUser.service_actions?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Action History</h3>
                    <div className="space-y-2">
                      {selectedUser.service_actions.slice().reverse().map((action, idx) => (
                        <div key={idx} className="border-l-2 border-violet-200 pl-3 py-1">
                          <p className="text-sm font-medium text-slate-700 capitalize">
                            {action.action.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-slate-500">
                            {action.performed_by_name} - {formatDate(action.timestamp)}
                          </p>
                          {action.reason && (
                            <p className="text-xs text-slate-400 mt-0.5">Reason: {action.reason}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Extend Trial Modal */}
      <AnimatePresence>
        {showExtendTrialModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowExtendTrialModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
              data-testid="extend-trial-modal"
            >
              <h3 className="text-lg font-bold text-slate-900 mb-4">Extend Trial</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Extension Days</label>
                  <div className="flex gap-2">
                    {[3, 5, 7].map(days => (
                      <button
                        key={days}
                        onClick={() => setExtendDays(days)}
                        className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                          extendDays === days
                            ? 'bg-violet-600 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        data-testid={`extend-${days}-days`}
                      >
                        +{days} days
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                  <textarea
                    value={extendReason}
                    onChange={(e) => setExtendReason(e.target.value)}
                    placeholder="Why are you extending this trial?"
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-violet-500 resize-none"
                    data-testid="extend-reason"
                  />
                </div>

                {actionError && (
                  <p className="text-red-600 text-sm">{actionError}</p>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowExtendTrialModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendTrial}
                  disabled={!extendReason || actionLoading}
                  className="flex-1 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-extend"
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Extend Trial
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Link Child Modal */}
      <AnimatePresence>
        {showLinkChildModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowLinkChildModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
              data-testid="link-child-modal"
            >
              <h3 className="text-lg font-bold text-slate-900 mb-4">Fix Parent-Child Link</h3>
              <p className="text-sm text-slate-500 mb-4">
                Link <strong>{selectedUser?.name || 'this user'}</strong> ({selectedUser?.role || 'unknown'}) with another account.
              </p>
              
              <div className="space-y-4">
                {/* Direction Toggle */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Link Direction</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLinkDirection('child')}
                      className={`flex-1 py-2 rounded-lg font-medium transition-colors text-sm ${
                        linkDirection === 'child'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      This user is Parent
                    </button>
                    <button
                      onClick={() => setLinkDirection('parent')}
                      className={`flex-1 py-2 rounded-lg font-medium transition-colors text-sm ${
                        linkDirection === 'parent'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      This user is Child
                    </button>
                  </div>
                </div>
                
                {linkDirection === 'child' ? (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Child&apos;s Email</label>
                    <input
                      type="email"
                      value={childEmail}
                      onChange={(e) => setChildEmail(e.target.value)}
                      placeholder="Enter child's account email"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-violet-500"
                      data-testid="child-email-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">The child account will be linked to {selectedUser?.name || 'this user'}</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Parent&apos;s Email</label>
                    <input
                      type="email"
                      value={parentEmail}
                      onChange={(e) => setParentEmail(e.target.value)}
                      placeholder="Enter parent's account email"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-violet-500"
                      data-testid="parent-email-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">{selectedUser?.name || 'This user'} will be linked as child to the parent</p>
                  </div>
                )}

                {actionError && (
                  <p className="text-red-600 text-sm">{actionError}</p>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowLinkChildModal(false);
                    setChildEmail('');
                    setParentEmail('');
                    setLinkDirection('child');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLinkChild}
                  disabled={(linkDirection === 'child' ? !childEmail : !parentEmail) || actionLoading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-link"
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Link Accounts
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
